import { Injectable } from "@angular/core";
import { Actions, concatLatestFrom, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { ActionWithPayload, batchActions } from "@obg/ngrx-utils";
import { PlatformService } from "@obg/ngx.global";
import { Log, LoggingFactory } from "@obg/ngx.global.evo";
import { interval, merge, of, timer } from "rxjs";
import {
	catchError,
	delay,
	exhaustMap,
	filter,
	map,
	mergeMap,
	retryWhen,
	switchMap,
	takeUntil,
	takeWhile,
	timeoutWith,
} from "rxjs/operators";

import { AppError } from "../../app-error/app-error.model";
import { AUTH_MESSAGE_CODES } from "../../auth/auth.const";
import { SESSION_ACTION_TYPE, SessionAction } from "../../auth/session.action";
import { CustomerNotVerifiedData } from "../../auth/session.model";
import { SessionSelector } from "../../auth/session.selector";
import { MessageScopeType, MessageType } from "../../message/message.model";
import { MessageService } from "../../message/message.service";
import { CoreState } from "../../state";
import { AUTH_TRANSACTION_CODES } from "../auth-transaction.const";
import { AuthenticationType, HintCode, VerificationStatus } from "../auth-transaction.model";

import {
	SMART_ID_AUTH_TRANSACTION_ACTION_TYPE,
	SmartIdAuthTransactionAction,
} from "./smart-id-auth-transaction.action";
import { SmartIdAuthTransactionConfigService } from "./smart-id-auth-transaction.config";
import { SMART_ID_AUTH_TRANSACTION_MESSAGE_CONTEXT } from "./smart-id-auth-transaction.const";
import {
	SmartIdInitializeSuccessPayload,
	SmartIdVerificationRequest,
	SmartIdVerificationStatusRequest,
	SmartIdVerificationStatusResponse,
} from "./smart-id-auth-transaction.model";
import { SmartIdAuthTransactionService } from "./smart-id-auth-transaction.service";

@Injectable()
export class SmartIdAuthTransactionEffect {
	loginFailedWithPersonalNumber$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SESSION_ACTION_TYPE.loginWithPersonalNumberFail),
			map((x: ActionWithPayload) => x.payload),
			filter(
				(payload) =>
					payload.code === AUTH_MESSAGE_CODES.login.customerNotVerified &&
					payload.data.customerNotVerifiedLoginResponse
			),
			map((payload) => payload.data.customerNotVerifiedLoginResponse as CustomerNotVerifiedData),
			filter((data) => !!data && !!data.personalNumber && !!data.transactionId),
			map((data) =>
				this.action.getVerificationStatus({
					token: data.token!,
					personalNumber: data.personalNumber!,
					transactionId: data.transactionId!,
				})
			)
		)
	);

	loginFail$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SESSION_ACTION_TYPE.loginFail),
			map((x: ActionWithPayload) => x.payload),
			filter(
				(payload) =>
					payload.code === AUTH_MESSAGE_CODES.login.customerNotVerified &&
					payload.data.customerNotVerifiedLoginResponse
			),
			map((payload) => payload.data.customerNotVerifiedLoginResponse as CustomerNotVerifiedData),
			filter((data) => !!data?.personalNumber),
			map((data) =>
				this.action.initializeVerification({ token: data.token, personalNumber: data.personalNumber! })
			)
		)
	);

	initializeAuthTransaction$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SMART_ID_AUTH_TRANSACTION_ACTION_TYPE.initializeAuthTransaction),
			map((x: ActionWithPayload) => x.payload),
			switchMap((request) =>
				this.service.initializeAuthTransaction(request).pipe(
					map((response) =>
						this.action.initializeAuthTransactionSuccess({
							autostartToken: response.autostartToken,
							transactionId: response.transactionId,
							authenticationType: AuthenticationType.SmartId,
							verificationCode: response.verificationCode,
						})
					),
					catchError((error: AppError) => {
						const errorCode = error.data.errors! ? error.data.errors[0].code : error.code;

						return (error.code === AUTH_TRANSACTION_CODES.authenticationError ||
							error.code === AUTH_TRANSACTION_CODES.validationError) &&
							errorCode
							? of(this.action.initializeAuthTransactionFail(new Error(errorCode)))
							: of(
									this.initializeFail(
										SMART_ID_AUTH_TRANSACTION_ACTION_TYPE.initializeAuthTransactionFail,
										error
									)
								);
					})
				)
			)
		)
	);

	initializeVerification$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SMART_ID_AUTH_TRANSACTION_ACTION_TYPE.initializeVerification),
			map((x: ActionWithPayload<SmartIdVerificationRequest>) => {
				const { token, personalNumber, transactionId, authenticationType } = x.payload;
				return {
					token,
					personalNumber,
					transactionId,
					authenticationType,
				};
			}),
			switchMap((request) =>
				this.service.initializeVerification(request).pipe(
					map((response) =>
						this.action.initializeVerificationSuccess({
							token: request.token,
							personalNumber: request.personalNumber,
							autostartToken: response.autostartToken,
							transactionId: response.transactionId,
							authenticationType: request.authenticationType,
						})
					),
					catchError((error: AppError) =>
						error.code === AUTH_TRANSACTION_CODES.validationError &&
						error.data.errors &&
						error.data.errors.length
							? of(this.action.initializeVerificationFail(new Error(error.data.errors[0].code)))
							: of(
									this.initializeFail(
										SMART_ID_AUTH_TRANSACTION_ACTION_TYPE.initializeVerificationFail,
										error
									)
								)
					)
				)
			)
		)
	);

	initializePolling$ = createEffect(() =>
		this.actions$.pipe(
			ofType(
				SMART_ID_AUTH_TRANSACTION_ACTION_TYPE.initializeVerificationSuccess,
				SMART_ID_AUTH_TRANSACTION_ACTION_TYPE.initializeAuthTransactionSuccess
			),
			map((x: ActionWithPayload<SmartIdInitializeSuccessPayload>) => x.payload),
			map((data) =>
				batchActions(
					this.action.getVerificationStatus({
						token: data.token,
						personalNumber: data.personalNumber,
						transactionId: data.transactionId,
						authenticationType: data.authenticationType,
					}),
					this.sessionAction.updateCustomerNotVerifiedData(data)
				)
			)
		)
	);

	getVerificationStatus$ = this.platformService.forClient(() =>
		createEffect(() =>
			this.actions$.pipe(
				ofType(SMART_ID_AUTH_TRANSACTION_ACTION_TYPE.getVerificationStatus),
				map((x: ActionWithPayload<SmartIdVerificationStatusRequest>) => x.payload),
				switchMap((request) =>
					merge(
						interval(this.config.getVerificationStatusInterval()).pipe(
							exhaustMap(() => this.service.getStatus(request)),
							takeWhile(
								(response: SmartIdVerificationStatusResponse) =>
									response.status === VerificationStatus.Pending,
								true
							),
							timeoutWith(
								this.config.getVerificationStatusTimeout(),
								of({
									status: VerificationStatus.Failed,
									hintCode: HintCode.Timeout,
								})
							),
							retryWhen((errors) =>
								errors.pipe(
									mergeMap((error, index) => {
										// IOS bug in WKWebView, which causes an xhr error
										if (error.data && error.data.isTrusted && index === 0) {
											return timer(0);
										}
										throw error;
									})
								)
							),
							map((response: SmartIdVerificationStatusResponse) =>
								this.mapVerificationResponse(response)
							),
							catchError((error: AppError) =>
								error.code === AUTH_TRANSACTION_CODES.validationError &&
								error.data.errors &&
								error.data.errors.length
									? of(this.action.getVerificationStatusFail(new Error(error.data.errors[0].code)))
									: of(
											this.initializeFail(
												SMART_ID_AUTH_TRANSACTION_ACTION_TYPE.getVerificationStatusFail,
												error
											)
										)
							),
							takeUntil(
								this.actions$.pipe(ofType(SMART_ID_AUTH_TRANSACTION_ACTION_TYPE.cancelLastVerification))
							)
						)
					)
				)
			)
		)
	);

	cancelLastVerification$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SMART_ID_AUTH_TRANSACTION_ACTION_TYPE.cancelLastVerification),
			concatLatestFrom(() => this.store.select(this.sessionSelector.getCustomerNotVerifiedData())),
			map(() =>
				this.mapVerificationFailedResponse({
					status: VerificationStatus.Failed,
					hintCode: HintCode.Cancelled,
				})
			)
		)
	);

	resetVerification$ = createEffect(() =>
		this.actions$.pipe(
			ofType(
				SESSION_ACTION_TYPE.login,
				SESSION_ACTION_TYPE.logout,
				SESSION_ACTION_TYPE.loginWithPersonalNumber,
				SESSION_ACTION_TYPE.loginWithVerificationCode
			),
			map(() => this.action.reset())
		)
	);

	clearOnLoginWithTokenSuccessRequest$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SESSION_ACTION_TYPE.loginWithTokenSuccess),
			delay(10000),
			map(() => this.action.reset())
		)
	);

	clearVerificationStatus$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SESSION_ACTION_TYPE.loginWithToken),
			map(() => this.action.resetVerificationStatus())
		)
	);

	initMessages$ = createEffect(() =>
		merge(
			this.messageService.mapErrorByContext$({
				[SMART_ID_AUTH_TRANSACTION_ACTION_TYPE.getVerificationStatusFail]:
					SMART_ID_AUTH_TRANSACTION_MESSAGE_CONTEXT.getVerificationStatus,
				[SMART_ID_AUTH_TRANSACTION_ACTION_TYPE.initializeVerificationFail]:
					SMART_ID_AUTH_TRANSACTION_MESSAGE_CONTEXT.initializeVerification,
				[SMART_ID_AUTH_TRANSACTION_ACTION_TYPE.initializeAuthTransactionFail]:
					SMART_ID_AUTH_TRANSACTION_MESSAGE_CONTEXT.initializeAuthTransaction,
				[SMART_ID_AUTH_TRANSACTION_ACTION_TYPE.cancelLastVerificationFail]:
					SMART_ID_AUTH_TRANSACTION_MESSAGE_CONTEXT.cancelLastVerification,
			}),
			this.messageService.mapClearByContext$({
				[SMART_ID_AUTH_TRANSACTION_ACTION_TYPE.getVerificationStatus]:
					SMART_ID_AUTH_TRANSACTION_MESSAGE_CONTEXT.getVerificationStatus,
				[SMART_ID_AUTH_TRANSACTION_ACTION_TYPE.initializeAuthTransaction]:
					SMART_ID_AUTH_TRANSACTION_MESSAGE_CONTEXT.initializeAuthTransaction,
				[SMART_ID_AUTH_TRANSACTION_ACTION_TYPE.initializeVerification]:
					SMART_ID_AUTH_TRANSACTION_MESSAGE_CONTEXT.initializeVerification,
				[SMART_ID_AUTH_TRANSACTION_ACTION_TYPE.cancelLastVerification]:
					SMART_ID_AUTH_TRANSACTION_MESSAGE_CONTEXT.cancelLastVerification,
			}),
			this.messageService.map$({
				[SMART_ID_AUTH_TRANSACTION_ACTION_TYPE.getVerificationStatusSuccess]: {
					context: SMART_ID_AUTH_TRANSACTION_ACTION_TYPE.getVerificationStatus,
					scope: MessageScopeType.Local,
					type: MessageType.Success,
					messageCode: AUTH_TRANSACTION_CODES.verificationStatus.completed,
				},
				[SMART_ID_AUTH_TRANSACTION_ACTION_TYPE.initializeAuthTransactionSuccess]: {
					context: SMART_ID_AUTH_TRANSACTION_ACTION_TYPE.initializeAuthTransaction,
					scope: MessageScopeType.Local,
					type: MessageType.Success,
					messageCode: AUTH_TRANSACTION_CODES.initializeAuthTransaction.success,
				},
				[SMART_ID_AUTH_TRANSACTION_ACTION_TYPE.cancelLastVerificationSuccess]: {
					context: SMART_ID_AUTH_TRANSACTION_ACTION_TYPE.cancelLastVerification,
					scope: MessageScopeType.Local,
					type: MessageType.Success,
					messageCode: AUTH_TRANSACTION_CODES.cancelLastVerification.success,
				},
				[SMART_ID_AUTH_TRANSACTION_ACTION_TYPE.getVerificationStatus]: {
					context: SMART_ID_AUTH_TRANSACTION_ACTION_TYPE.getVerificationStatus,
					scope: MessageScopeType.Local,
					type: MessageType.Info,
					messageCode: AUTH_TRANSACTION_CODES.verificationStatus.start,
				},
			})
		)
	);

	private logger: Log;

	constructor(
		private actions$: Actions,
		private action: SmartIdAuthTransactionAction,
		private service: SmartIdAuthTransactionService,
		private config: SmartIdAuthTransactionConfigService,
		private platformService: PlatformService,
		private messageService: MessageService,
		private sessionAction: SessionAction,
		private sessionSelector: SessionSelector,
		private store: Store<CoreState>,
		loggingFactory: LoggingFactory
	) {
		this.logger = loggingFactory.get("SmartIdAuthTransactionEffect");
	}

	private initializeFail(actionType: string, error: AppError | Error): ActionWithPayload {
		switch (actionType) {
			case SMART_ID_AUTH_TRANSACTION_ACTION_TYPE.initializeAuthTransactionFail:
				this.logger.warn("initializeAuthTransaction$", JSON.stringify(error));
				return this.action.initializeAuthTransactionFail(error);

			case SMART_ID_AUTH_TRANSACTION_ACTION_TYPE.initializeVerificationFail:
				this.logger.warn("initializeVerification$", JSON.stringify(error));
				return this.action.initializeVerificationFail(error);

			default:
				this.logger.warn("getVerificationStatus$", JSON.stringify(error));
				return this.action.getVerificationStatusFail(error);
		}
	}

	private mapVerificationFailedResponse(response: SmartIdVerificationStatusResponse): ActionWithPayload<any> {
		let error = AUTH_TRANSACTION_CODES.verificationStatus.failed.generic;

		switch (response.hintCode) {
			case HintCode.Timeout:
				error = AUTH_TRANSACTION_CODES.verificationStatus.failed.timeout;
				break;
			case HintCode.AlreadyInProgress:
				error = AUTH_TRANSACTION_CODES.verificationStatus.failed.alreadyInProgress;
				break;
			case HintCode.Cancelled:
				error = AUTH_TRANSACTION_CODES.verificationStatus.failed.cancelled;
				break;
			case HintCode.CertificateError:
				error = AUTH_TRANSACTION_CODES.verificationStatus.failed.certificateError;
				break;
			case HintCode.ExpiredTransaction:
				error = AUTH_TRANSACTION_CODES.verificationStatus.failed.expiredTransaction;
				break;
			case HintCode.ProviderError:
				error = AUTH_TRANSACTION_CODES.verificationStatus.failed.providerError;
				break;
			case HintCode.StartFailed:
				error = AUTH_TRANSACTION_CODES.verificationStatus.failed.startFailed;
				break;
			case HintCode.Unknown:
				error = AUTH_TRANSACTION_CODES.verificationStatus.failed.unknown;
				break;
			case HintCode.UserCancel:
				error = AUTH_TRANSACTION_CODES.verificationStatus.failed.userCancel;
		}

		return this.action.getVerificationStatusFail(new Error(error));
	}

	private mapVerificationResponse(response: SmartIdVerificationStatusResponse): ActionWithPayload {
		switch (response.status) {
			case VerificationStatus.Completed:
				return this.action.getVerificationStatusSuccess(response);

			case VerificationStatus.Failed:
				return this.mapVerificationFailedResponse(response);

			default:
				return this.action.getVerificationStatusPending(response);
		}
	}
}
